import * as LodashFP from 'lodash/fp'
import * as React from 'react'

import {
  Box,
  Badge,
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  List,
  ListItem,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  Collapse,
  styled,
  Switch,
} from '@design/pt-ui'

import { SubtitlesOutlined as SubtitlesOutlinedIcon } from '@mui/icons-material'

import Dropzone, { FileRejection } from 'react-dropzone'

import { toast } from 'react-toastify'

import SimpleModal from '../../components/SimpleModal'

import '../../utils/number-locale'
import numeral from '../../utils/number-locale'

import { OnChangeValue } from 'react-select'
import DI from '../../app/di'
import CheckBoxList from '../../components/CheckBoxList'
import CollapseButton from '../../components/CollapseButton'
import DebouncedNumericInput from '../../components/DebouncedNumericInput'
import DebouncedTextField from '../../components/DebouncedTextField'
import GifPlayer from '../../components/GifPlayer'
import Loader from '../../components/Loader'
import OptionGrid from '../../components/OptionGrid/OptionGrid'
import Select from '../../components/Select'
import SelectArrows from '../../components/SelectArrows'
import VideoPreview from '../../components/VideoPlayer'
import {
  FieldGuidelinesType,
  FieldValidation,
  FormBlueprint,
  FormFieldBlueprint,
  FormFieldFile,
  FormFieldSelect,
  Option,
} from '../../interfaces/components/dynamic-form'
import { Creative, CreativeId } from '../../interfaces/entities/creative'
import { FileId, IFilesService } from '../../interfaces/services/files'
import { State as IState } from '../../model'
import { IStringAnyMap } from '../../types/map'
import { translateWithUserLocale } from '../../utils/i18n'
import {
  KeyPath,
  pathForClassName as pathForClassNameFn,
} from '../../utils/object'
import FieldGuidelinesComponent from './FieldGuidelines'

const CntHolder = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  flexFlow: 'row',
  alignItems: 'center',
  color: theme.palette.primary.dark,
  padding: '10px 0',
}))

const cntHolderSx = {
  display: 'flex',
  flexWrap: 'wrap',
  flexFlow: 'row',
  alignItems: 'center',
  color: 'primary.dark',
  padding: '10px 0',
} as const

const FlexCentered = styled('span')({
  display: 'flex',
  alignItems: 'center',
})

const FlexValidationList = styled('span')({
  display: 'flex',
  flexFlow: 'column',
})

const LabelCntHolder = styled('div')(({ theme }) => ({
  display: 'flex',
  flexFlow: 'row',
  alignItems: 'center',
  color: theme.palette.primary.dark,
}))

const StatusIconFF = styled(Icon, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: 'check' | 'warning' | 'error' | 'info' }>(({ theme, tone }) => ({
  fontSize: tone === 'info' ? 16 : 44,
  marginRight: tone === 'info' ? 0 : theme.spacing(2),
  marginLeft: tone === 'info' ? theme.spacing(1.5) : 0,
  color:
    tone === 'check'
      ? '#57ac40'
      : tone === 'warning'
        ? '#ffb37a'
        : tone === 'error'
          ? 'red'
          : 'grey',
}))

const WarningText = styled('p')(({ theme }) => ({
  fontSize: 14,
  fontWeight: 400,
  color: theme.palette.warning.main,
}))

const ErrorText = styled('span')({
  fontSize: 14,
  fontWeight: 400,
  color: 'red',
})

const UnknownType = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  flexFlow: 'row',
  marginTop: '25px',
  alignItems: 'center',
  color: theme.palette.primary.dark,
}))

const DropZoneHolder = styled('div')({
  minHeight: 200,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
})

const InfoHolder = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 9999,
})

const CounterBadge = styled('span', {
  shouldForwardProp: (prop) => prop !== 'warning',
})<{ warning?: boolean }>(({ theme, warning }) => ({
  position: 'absolute',
  fontSize: 14,
  backgroundColor: warning ? theme.palette.warning.light : '#57ac40',
  marginLeft: theme.spacing(2),
  color: '#fff',
  padding: '2px 5px',
  fontWeight: 700,
  zIndex: 10,
  right: 0,
  top: '-10px',
  borderRadius: 4,
}))

interface ValidationElementProps {
  validation: FieldValidation
}
export const ValidationElement = (props: ValidationElementProps) => {
  const { validation } = props

  const getValidationElementErrorOrWarning = (validation: any) => {
    const validationErrorWarning = LodashFP.cond([
      [
        (val: typeof validation) => val.status === 'error',
        (val: typeof validation) => (
          <FlexCentered>
            <StatusIconFF tone="error">warning</StatusIconFF>
            <ErrorText>{val.status === 'error' && val.message}</ErrorText>
          </FlexCentered>
        ),
      ],
      [
        (val: typeof validation) => val.status === 'warning',
        (val: typeof validation) => (
          <FlexCentered>
            <StatusIconFF tone="warning">priority_high</StatusIconFF>
            <WarningText>{val.status === 'warning' && val.message}</WarningText>
          </FlexCentered>
        ),
      ],
    ])(validation)

    return validationErrorWarning
  }

  return (
    <div>
      {validation.map((msg, i) => {
        const validationElement =
          msg.status && msg.status === 'ok' ? (
            <FlexCentered key={i}>
              <StatusIconFF tone="check">check</StatusIconFF>
            </FlexCentered>
          ) : (
            <FlexValidationList key={i}>
              {getValidationElementErrorOrWarning(msg)}
            </FlexValidationList>
          )

        return validationElement
      })}
    </div>
  )
}

interface IFormFieldProps {
  blueprint: FormFieldBlueprint
  disabled: boolean
  disabledOptionTooltip?: string
  value: any
  path: KeyPath
  locked?: boolean
  validation: FieldValidation
  guidelines: FieldGuidelinesType
  backgroundColor?: string
  onPendingChange?: (pending: boolean) => any
  onChange: (value: any, field: string) => any
  // Stupid hack because file service is too tightly coupled to domain
  creativeId?: string
  creative?: Creative
  formBlueprint: FormBlueprint
  collapsable?: boolean
  initiallyCollapsed?: boolean
  platform: string
  index?: number
}

interface IFormFieldState {
  guidelinesShown: boolean
  fieldCollapsed: boolean
  file: {
    uploading: boolean
    preview: string | null
    fileInfo: IStringAnyMap | null
  }
  text: {
    length: number
  }
}

export function FormFieldComponent(props: IFormFieldProps) {
  const [state, setState] = React.useState<IFormFieldState>({
    guidelinesShown: false,
    fieldCollapsed: props.initiallyCollapsed || false,
    file: {
      uploading: false,
      preview: null,
      fileInfo: null,
    },

    text: {
      length: LodashFP.getOr('0', 'value.length', props),
    },
  })

  const pathForClassName = pathForClassNameFn(props.path)

  const getPreview = async (props: IFormFieldProps) => {
    if (props.blueprint.type === 'file' && props.value) {
      const response = await getFilesService().getFileInfo(props.value)
      if (response.type === 'success') {
        setState((state) => ({
          ...state,
          file: { ...state.file, fileInfo: response.payload.meta },
        }))
      }
    }
  }

  const showGuidelines = () => {
    setState((state) => ({ ...state, guidelinesShown: true }))
  }

  const hideGuidelines = () => {
    setState((state) => ({ ...state, guidelinesShown: false }))
  }

  const toggleFieldCollapsed = () => {
    setState((state) => ({
      ...state,
      fieldCollapsed: !state.fieldCollapsed,
    }))
  }

  const handleTextChange = (value: string) => {
    if (props.disabled || props.locked) {
      return
    }

    props.onChange(value, props.blueprint.name)
  }

  const handleBooleanChange = (value: string) => {
    if (props.disabled || props.locked) {
      return
    }

    const val = value === 'on' ? true : false
    props.onChange(val, props.blueprint.name)
  }

  const handleSelectChange = (value: OnChangeValue<any, boolean>) => {
    if (props.disabled || props.locked) {
      return
    }
    props.onChange(value.value, props.blueprint.name)
  }

  const handleIntegerChange = (ivalue: number | null) => {
    if (props.disabled || props.locked) {
      return
    }

    const value = ivalue === 0 ? null : ivalue
    const min: number | undefined = LodashFP.getOr(
      null,
      ['blueprint', 'meta', 'min'],
      props,
    )
    if (min) {
      const newValue = value && value < min ? min : value
      props.onChange(newValue, props.blueprint.name)
    } else {
      props.onChange(value, props.blueprint.name)
    }
  }

  const handleOptionGridChange = (value: any) => {
    if (props.disabled || props.locked) {
      return
    }

    const isAnyFieldDisabled = (
      props.blueprint as FormFieldSelect
    ).meta.options.some((option) => option.disabled)
    if (isAnyFieldDisabled && value.length === 0) {
      return
    }

    const v = (props.blueprint as FormFieldSelect).meta.multiple
      ? value
      : value[0]
    props.onChange(v, props.blueprint.name)
  }

  const handleMultiselectChange = (value: OnChangeValue<any, boolean>) => {
    if (props.disabled || props.locked) {
      return
    }

    props.onChange(
      value.map((v: any) => v.value),
      props.blueprint.name,
    )
  }

  const handleCheckboxChange = (value: any) => {
    if (props.disabled || props.locked) {
      return
    }
    props.onChange(value, props.blueprint.name)
  }

  const handleSelectArrowsChange = (value: any) => {
    if (props.disabled || props.locked) {
      return
    }
    props.onChange(value, props.blueprint.name)
  }

  const handleRadioButtonChange = (value: any) => {
    if (props.disabled || props.locked) {
      return
    }

    props.onChange([value], props.blueprint.name)
  }

  const getFilesService: () => IFilesService = () => {
    return DI.get('FilesService')
  }

  const handleFileDrop = async (
    acceptedFiles: File[],
    _rejectedFiles: FileRejection[],
  ) => {
    if (props.disabled || props.locked) {
      return
    }

    if (acceptedFiles.length <= 0) {
      return
    }
    const blueprint = props.blueprint as FormFieldFile
    const file = acceptedFiles[0]

    if (props.guidelines.requirements.unconditional.size.max) {
      if (
        props.platform === 'facebook' &&
        file.type.startsWith('video/') &&
        file.size > props.guidelines.requirements.unconditional.size.max
      ) {
        toast.error(
          translateWithUserLocale('file_controls.max_video_size_exceeded', {
            size: props.guidelines.requirements.unconditional.size.max / 1000000
          }),
        )
        return
      }
    }

    try {
      setState((state) => ({
        ...state,
        file: {
          ...state.file,
          uploading: true,
          preview: URL.createObjectURL(file),
          fileInfo: {
            type: file.type,
          },
        },
      }))
      const uploaded = await getFilesService().uploadFile(
        file,
        props.creativeId || '',
      )

      if (uploaded.type === 'error') {
        toast.error(
          translateWithUserLocale('file_controls.error_uploading', {
            error: uploaded.payload.message,
          }),
        )
        return setState((state) => ({
          ...state,
          file: {
            ...state.file,
            uploading: false,
            preview: null,
          },
        }))
      }
      setState((state) => ({
        ...state,
        file: {
          ...state.file,
          uploading: false,
          fileInfo: uploaded.payload.meta,
        },
      }))

      const value = blueprint.meta.multiple
        ? [uploaded.payload.id]
        : uploaded.payload.id
      props.onChange(value, blueprint.name)
    } catch (e) {
      setState((state) => ({
        ...state,
        file: {
          ...state.file,
          preview: null,
        },
      }))
    }
  }

  const handleFileDelete = (id: FileId) => {
    if (props.disabled || props.locked) {
      return
    }
    const blueprint = props.blueprint as FormFieldFile
    // when we delete subtitle file from the form we need to remove it from selected video from FB,
    // because it's uploaded to FB in order to get proper preview
    if (blueprint.meta.fileType === 'subtitle') {
      getFilesService().deleteFile(id)
    }

    setState((state) => ({
      ...state,
      file: {
        ...state.file,
        uploading: false,
        preview: null,
        fileInfo: null,
      },
    }))
    props.onChange(null, blueprint.name)
  }

  const renderFilePreview = (
    preview: string,
    id: FileId,
    fileInfo: IStringAnyMap | null,
  ) => {
    const thumbnailUrl = getFilesService().getFileThumbnailUrl(id)

    if (fileInfo == null) {
      return (
        <Loader
          message={translateWithUserLocale(
            'loaders.uploading_and_saving_assets',
          )}
        />
      )
    }

    const mimeType = LodashFP.getOr('image/jpeg', 'mime_type', fileInfo)
    const isSubtitle = fileInfo.name.endsWith('.srt')
    const badgeSx = { background: '#dadada', width: 36, height: 36 }
    const deleteIcon = (
      <IconButton
        color="secondary"
        sx={{ margin: 1 }}
        disabled={props.disabled}
        onClick={() => handleFileDelete(id)}
        aria-label="Delete">
        <Icon sx={{ fontSize: '26px !important' }}>clear</Icon>
      </IconButton>
    )

    if (mimeType === 'image/gif') {
      return (
        <DropZoneHolder>
          <Badge
            sx={{ '& .MuiBadge-badge': badgeSx }}
            color="default"
            badgeContent={deleteIcon}>
            <div>
              <GifPlayer
                autoplay={false}
                style={{ width: 'auto', height: '200px' }}
                gif={preview}
                still={thumbnailUrl}
              />
            </div>
          </Badge>
        </DropZoneHolder>
      )
    }

    if (mimeType.startsWith('image/')) {
      return (
        <DropZoneHolder>
          <Badge
            sx={{ '& .MuiBadge-badge': badgeSx }}
            color="default"
            badgeContent={deleteIcon}>
            <div>
              <img
                style={{ width: 'auto', height: '200px' }}
                src={preview}
                alt=""
              />
            </div>
          </Badge>
        </DropZoneHolder>
      )
    }

    if (fileInfo != null && mimeType.startsWith('video/')) {
      return (
        <div
          style={{
            position: 'relative',
            height: 200,
            maxWidth: 400,
            minWidth: 200,
            flexBasis: '40%',
          }}>
          <Badge
            sx={{
              margin: 2,
              position: 'absolute',
              right: '-12px',
              top: '-16px',
              '& .MuiBadge-badge': badgeSx,
            }}
            color="default"
            badgeContent={deleteIcon}>
            <div />
          </Badge>
          <VideoPreview
            height={200}
            thumbnail={fileInfo.thumbnail}
            src={preview}
          />
        </div>
      )
    }

    if (fileInfo != null && isSubtitle) {
      return (
        <DropZoneHolder>
          <Badge
            sx={{
              '& .MuiBadge-badge': { ...badgeSx, right: 20, top: 30 },
            }}
            color="default"
            badgeContent={deleteIcon}>
            <SubtitlesOutlinedIcon
              sx={{ width: 'auto', height: '200px', color: '#bebebe' }}
            />
          </Badge>
        </DropZoneHolder>
      )
    }

    return null
  }

  const renderLoader = (typeMov: boolean) => {
    return (
      <DropZoneHolder>
        <Loader
          variant="flex"
          size="medium"
          message={
            typeMov
              ? translateWithUserLocale('loaders.mov_converted')
              : translateWithUserLocale('loaders.uploading_and_saving_assets')
          }
        />
      </DropZoneHolder>
    )
  }

  const renderDropzone = (blueprint: FormFieldFile) => {
    return (
      <DropZoneHolder>
        <Dropzone
          disabled={props.disabled}
          onDrop={handleFileDrop}
          multiple={blueprint.meta.multiple}
          accept={blueprint.meta.allowedTypes.reduce<{
            [mime: string]: string[]
          }>((acc, mime) => ({ ...acc, [mime]: [] }), {})}>
          {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
            <div
              {...getRootProps({
                className: 'cp-dropzone',
                style: {
                  border: '1px dashed #dadada',
                  borderRadius: 6,
                  padding: 40,
                  width: '100%',
                },
              })}>
              <input {...getInputProps()} />
              {isDragActive ? (
                <div>
                  {translateWithUserLocale('file_controls.drop_file_here')}.
                </div>
              ) : isDragReject ? (
                <div>
                  {translateWithUserLocale('file_controls.cannot_drop_file')}.
                </div>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexFlow: 'column',
                    alignItems: 'center',
                    height: '100%',
                  }}>
                  <Icon sx={{ fontSize: 56, marginBottom: 2 }} color="inherit">
                    unarchive
                  </Icon>
                  <Typography
                    sx={{ marginBottom: 2, fontSize: 14, fontWeight: 600 }}
                    color="textSecondary">
                    {translateWithUserLocale(
                      'file_controls.drag_and_drop_here',
                    )}
                  </Typography>
                  <Typography
                    sx={{ marginBottom: 2, fontSize: 14, fontWeight: 600 }}
                    color="textSecondary">
                    {translateWithUserLocale('or')}
                  </Typography>

                  <Button
                    sx={{ textTransform: 'none', marginTop: 2 }}
                    disabled={props.disabled}
                    variant="contained"
                    color="primary">
                    {translateWithUserLocale('file_controls.browse_file')}
                  </Button>
                </Box>
              )}
            </div>
          )}
        </Dropzone>
      </DropZoneHolder>
    )
  }

  React.useEffect(() => {
    getPreview(props)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if ((state.file.preview || state.file.fileInfo) && !state.file.uploading) {
      setState((state) => ({
        ...state,
        file: {
          preview: null,
          fileInfo: null,
          uploading: false,
        },
      }))
    }

    getPreview(props)
  }, [props.value]) // eslint-disable-line react-hooks/exhaustive-deps

  const storeState: () => IState = () => DI.get('store').getState()
  const user = storeState().user
  const { value, blueprint } = props

  const label =
    blueprint.label == null || blueprint.label === ''
      ? blueprint.name
      : blueprint.label

  const description =
    blueprint.description == null || blueprint.description === ''
      ? null
      : blueprint.description
  if (blueprint.type === 'fieldset') {
    // TODO: Log a warning or something, this code should never execute
    return null
  }

  if (blueprint.type === 'list') {
    // TODO: Log a warning or something, this code should never execute
    return null
  }

  const labelElement = (
    <LabelCntHolder>
      {props.collapsable && (
        <CollapseButton
          onClick={toggleFieldCollapsed}
          collapsed={state.fieldCollapsed}
        />
      )}
      <InputLabel sx={{}} htmlFor={blueprint.name}>
        {blueprint.mandatory
          ? label
          : label + ` (${translateWithUserLocale('optional')})`}
      </InputLabel>
      <InfoHolder>
        {description != null ? (
          <Tooltip title={description} placement="top">
            <StatusIconFF tone="info">info</StatusIconFF>
          </Tooltip>
        ) : null}
        <span style={{ paddingLeft: 10 }}>
          <ValidationElement validation={props.validation} />
        </span>
      </InfoHolder>
    </LabelCntHolder>
  )

  if (blueprint.type === 'text') {
    return (
      <Grid
        className={`cp-dynamic-field cp-dynamic-field-type-text cp-dynamic-field-path@${pathForClassName}`}
        container={true}
        alignItems="center"
        sx={{ marginTop: '15px' }}
        spacing={1}>
        <Grid
          size={{
            md: 12,
          }}>
          {labelElement}
        </Grid>
        <Grid
          size={{
            md: 12,
          }}>
          <Collapse in={!state.fieldCollapsed}>
            <CntHolder>
              <Grid
                size={{
                  md: 12,
                }}>
                <FormControl fullWidth={true} margin={'dense'}>
                  {blueprint?.meta?.maximumCharacters ? (
                    <Box sx={{ position: 'relative' }}>
                      <CounterBadge
                        warning={
                          state.text.length > blueprint.meta.maximumCharacters
                        }
                        className={
                          state.text.length > blueprint.meta.maximumCharacters
                            ? 'cp-text-input-counter cp-warning'
                            : 'cp-text-input-counter cp-ok'
                        }>
                        {state.text.length} / {blueprint.meta.maximumCharacters}
                      </CounterBadge>
                    </Box>
                  ) : null}
                  <DebouncedTextField
                    id={`cp-${LodashFP.snakeCase(blueprint.name)}-input`}
                    text={{
                      variant: 'outlined',
                      disabled: props.disabled,
                    }}
                    locked={props.locked}
                    value={value}
                    onPendingChange={props.onPendingChange}
                    onChange={handleTextChange}
                    onLengthChange={(len) => {
                      setState((state) => ({
                        ...state,
                        text: {
                          length: len,
                        },
                      }))
                    }}
                    debounce={2000}
                  />
                </FormControl>
              </Grid>
            </CntHolder>
          </Collapse>
        </Grid>
      </Grid>
    )
  }

  if (blueprint.type === 'boolean') {
    return (
      <Grid
        container={true}
        spacing={1}
        className={`cp-dynamic-field cp-dynamic-field-type-bool cp-dynamic-field-path@${pathForClassName}`}>
        <Grid
          size={{
            md: 12,
          }}>
          <CntHolder>
            <Switch
              state={value ? 'on' : 'off'}
              onChange={handleBooleanChange}
              disabled={props.disabled || props.locked || false}
              style={{ marginRight: '6px' }}
            />
            {labelElement}
          </CntHolder>
        </Grid>
      </Grid>
    )
  }

  if (blueprint.type === 'integer') {
    return (
      <Grid
        container={true}
        spacing={1}
        className={`cp-dynamic-field cp-dynamic-field-type-integer cp-dynamic-field-path@${pathForClassName}`}>
        <Grid
          size={{
            md: 12,
          }}>
          {labelElement}
        </Grid>
        <Grid
          size={{
            md: 12,
          }}>
          <Collapse in={!state.fieldCollapsed}>
            <CntHolder>
              <Grid
                size={{
                  md: 12,
                }}>
                <FormControl fullWidth={true} margin={'dense'}>
                  <DebouncedNumericInput
                    value={value}
                    onPendingChange={props.onPendingChange}
                    onChange={handleIntegerChange}
                    min={0}
                    max={blueprint.meta.max}
                    step={1}
                    disabled={props.locked ? true : false}
                    debounce={2000}
                  />
                </FormControl>
              </Grid>
            </CntHolder>
          </Collapse>
        </Grid>
      </Grid>
    )
  }

  if (blueprint.type === 'select') {
    if (blueprint.meta.options == null) {
      return (
        <UnknownType>
          <StatusIconFF tone="warning">priority_high</StatusIconFF>
          <WarningText>No options provided</WarningText>
        </UnknownType>
      )
    }

    if (blueprint.meta.as === 'arrows') {
      return (
        <Grid
          container={true}
          spacing={1}
          className={`cp-dynamic-field cp-dynamic-field-type-select cp-dynamic-field-type-select-as-arrows cp-dynamic-field-path@${pathForClassName}`}
          sx={cntHolderSx}>
          <Grid
            size={{
              md: 12,
            }}>
            {labelElement}
            <Collapse in={!state.fieldCollapsed}>
              <FormControl fullWidth={true}>
                <div>
                  <SelectArrows
                    disabled={
                      props.disabled || blueprint.meta.options.length === 0
                    }
                    options={blueprint.meta.options.filter(
                      (opt) => !opt.disabled,
                    )}
                    value={value}
                    onChange={handleSelectArrowsChange}
                  />
                </div>
              </FormControl>
            </Collapse>
          </Grid>
        </Grid>
      )
    }

    if (blueprint.meta.as === 'grid') {
      return (
        <Grid
          container={true}
          spacing={3}
          className={`cp-dynamic-field cp-dynamic-field-type-select cp-dynamic-field-type-select-as-grid cp-dynamic-field-path@${pathForClassName}`}
          sx={cntHolderSx}>
          <Grid
            size={{
              md: 12,
            }}>
            {labelElement}
            <Collapse in={!state.fieldCollapsed}>
              <FormControl fullWidth={true} margin={'dense'}>
                <div>
                  <OptionGrid
                    disabled={
                      props.disabled || blueprint.meta.options.length === 0
                    }
                    disabledOptionTooltip={props.disabledOptionTooltip}
                    multiple={blueprint.meta.multiple}
                    options={blueprint.meta.options}
                    error={false}
                    selected={
                      value === null
                        ? []
                        : LodashFP.isArray(value)
                          ? value
                          : [value]
                    }
                    onChange={handleOptionGridChange}
                  />
                </div>
              </FormControl>
            </Collapse>
          </Grid>
        </Grid>
      )
    }

    if (blueprint.meta.as === 'checkboxes') {
      return (
        <Grid
          container={true}
          spacing={3}
          className={`cp-dynamic-field cp-dynamic-field-type-select cp-dynamic-field-type-select-as-checkboxes cp-dynamic-field-path@${pathForClassName}`}
          sx={cntHolderSx}>
          <Grid
            size={{
              md: 12,
            }}>
            {labelElement}
            <Collapse in={!state.fieldCollapsed}>
              <FormControl fullWidth={true}>
                <div>
                  <FormGroup>
                    <CheckBoxList
                      disabled={
                        props.disabled || blueprint.meta.options.length === 0
                      }
                      disabledOptionTooltip={props.disabledOptionTooltip}
                      options={blueprint.meta.options}
                      value={value}
                      onChange={handleCheckboxChange}
                      multiple={blueprint.meta.multiple}
                    />
                  </FormGroup>
                </div>
              </FormControl>
            </Collapse>
          </Grid>
        </Grid>
      )
    }

    if (blueprint.meta.as === 'radiobutton') {
      return (
        <Grid
          container={true}
          spacing={1}
          className={`cp-dynamic-field cp-dynamic-field-type-radio cp-dynamic-field-type-select-as-radio cp-dynamic-field-path@${pathForClassName}`}
          sx={cntHolderSx}>
          <Grid
            size={{
              md: 12,
            }}>
            {labelElement}
            <FormControl fullWidth={true}>
              <div>
                <RadioGroup
                  aria-label="formats"
                  name="formats"
                  value={value[0]}
                  onChange={(e) => handleRadioButtonChange(e.target.value)}>
                  {blueprint.meta.options.map((option, i) => {
                    return (
                      <FormControlLabel
                        disabled={
                          option.disabled ||
                          props.disabled ||
                          blueprint.meta.options.length === 0
                        }
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                      />
                    )
                  })}
                </RadioGroup>
              </div>
            </FormControl>
          </Grid>
        </Grid>
      )
    }

    const filteredOptions = blueprint.meta.options.filter((option) => {
      return !option.disabled
    })

    // Must be dropdown
    if (blueprint.meta.multiple) {
      return (
        <Grid
          container={true}
          spacing={1}
          className={`cp-dynamic-field cp-dynamic-field-type-select cp-dynamic-field-type-select-as-dropdown cp-dynamic-field-path@${pathForClassName}`}
          sx={cntHolderSx}>
          <Grid
            size={{
              md: 12,
            }}>
            {labelElement}
            <FormControl fullWidth={true}>
              <div>
                <Select
                  isDisabled={props.disabled || filteredOptions.length === 0}
                  backgroundColor={props.backgroundColor}
                  isMulti={true}
                  options={filteredOptions}
                  isOptionDisabled={(option: Option) =>
                    option.disabled || false
                  }
                  value={
                    value &&
                    value.map((v: any) => {
                      const option = filteredOptions.find((o) => o.value === v)

                      return {
                        value: v,
                        label: option ? option.label : v,
                      }
                    })
                  }
                  onChange={handleMultiselectChange}
                />
              </div>
            </FormControl>
          </Grid>
        </Grid>
      )
    }

    return (
      <Grid
        sx={{ ...cntHolderSx, paddingTop: '20px' }}
        container={true}
        className={`cp-dynamic-field cp-dynamic-field-type-select cp-dynamic-field-type-select-as-dropdown cp-dynamic-field-path@${pathForClassName}`}>
        <Grid
          size={{
            md: 12,
          }}>
          {labelElement}
          <FormControl fullWidth={true}>
            <Select
              isDisabled={props.disabled || filteredOptions.length === 0}
              isMulti={false}
              options={filteredOptions}
              isOptionDisabled={(option: Option & { disabled?: boolean }) =>
                option.disabled || false
              }
              backgroundColor={props.backgroundColor}
              value={
                filteredOptions.find((o) => o.value === value) || {
                  value,
                  label: value,
                }
              }
              onChange={handleSelectChange}
            />
          </FormControl>
        </Grid>
      </Grid>
    )
  }

  if (blueprint.type === 'file') {
    const preview =
      state.file.preview && value != null
        ? state.file.preview
        : !value
          ? null
          : getFilesService().getFileUrl(value)
    const isVideoSubtitle =
      blueprint.name === 'video_subtitle' ||
      blueprint.name === 'platform_customization_subtitle'
    return (
      <Grid
        className={`cp-dynamic-field cp-dynamic-field-type-file cp-dynamic-field-path@${pathForClassName}`}
        container={true}
        alignItems="center"
        sx={{ marginTop: '15px' }}
        spacing={3}>
        <Grid
          size={{
            md: 12,
          }}>
          {labelElement}
        </Grid>
        <Collapse
          in={!state.fieldCollapsed}
          sx={{
            '& .MuiCollapse-wrapper': {
              paddingLeft: '20px',
              marginTop: '25px',
              marginBottom: '25px',
            },
          }}>
          <Grid
            size={{
              md: 12,
            }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}>
              {preview !== null && !state.file.uploading
                ? renderFilePreview(preview, value, state.file.fileInfo)
                : state.file.uploading
                  ? renderLoader(
                      state?.file?.fileInfo?.type === 'video/quicktime',
                    )
                  : renderDropzone(blueprint)}

              <Box
                sx={{
                  display: 'flex',
                  height: '100%',
                  flexFlow: 'column',
                  fontWeight: 600,
                  marginLeft: '20px',
                  '& ul': {
                    '& li': {
                      fontWeight: 600,
                    },
                  },
                }}>
                <Typography component="div">
                  <FileInfoList {...state} isVideoSubtitle={isVideoSubtitle} />
                </Typography>
                {value && user.groups.includes('memoria-internal') && (
                  <FileDownloadUrl
                    id={value}
                    creativeId={props.creativeId}
                    slideOrder={
                      typeof props.index === 'number' ? props.index + 1 : null
                    }
                  />
                )}
              </Box>

              {blueprint.name.includes('thumbnail') || isVideoSubtitle ? (
                ''
              ) : (
                <Button
                  className="cp-action-show-guidelines"
                  variant="outlined"
                  color="inherit"
                  onClick={showGuidelines}>
                  {translateWithUserLocale(
                    'dynamic_form.guidelines.modal.button',
                  )}
                </Button>
              )}
              <SimpleModal
                width={'auto'}
                title={translateWithUserLocale(
                  'dynamic_form.guidelines.modal.title',
                )}
                open={state.guidelinesShown}
                onClose={hideGuidelines}
                scroll="paper">
                <FieldGuidelinesComponent
                  creative={props.creative}
                  guidelines={props.guidelines}
                  fieldBlueprint={blueprint}
                  formBlueprint={props.formBlueprint}
                  platform={props.platform}
                />
              </SimpleModal>
            </div>
          </Grid>
        </Collapse>
      </Grid>
    )
  }

  return null
}

const FileInfoList = (
  props: IFormFieldState & { isVideoSubtitle: boolean },
) => {
  const { file, isVideoSubtitle } = props
  return (
    file.fileInfo &&
    (!isVideoSubtitle ? (
      <List>
        <ListItem>
          width: {numeral(file.fileInfo.width).format('0,0') + ' pixels'}
        </ListItem>
        <ListItem>
          height: {numeral(file.fileInfo.height).format('0,0') + ' pixels'}
        </ListItem>
        <ListItem>size: {numeral(file.fileInfo.size).format('0b')}</ListItem>
        <ListItem>ratio: {file.fileInfo.ratio}</ListItem>
      </List>
    ) : (
      <List>
        <ListItem>name:{` ${file.fileInfo.name}`}</ListItem>
        <ListItem>size: {numeral(file.fileInfo.size).format('0b')}</ListItem>
      </List>
    ))
  )
}

export const FileDownloadUrl = (props: {
  id: FileId
  creativeId: CreativeId | undefined
  slideOrder: number | null
}) => {
  const filesService = DI.get('FilesService') as IFilesService
  return (
    <div style={{ paddingLeft: 14 }} className="file-download">
      <a
        style={{ textDecoration: 'none' }}
        href={filesService.getFileDownloadUrl(
          props.id,
          props.creativeId,
          props.slideOrder,
        )}>
        <Button
          size="medium"
          sx={{
            color: 'text.primary',
            borderColor: 'text.primary',
          }}>
          <Icon sx={{ mr: '8px' }} fontSize="small">
            save_alt
          </Icon>
          {translateWithUserLocale('download')}
        </Button>
      </a>
    </div>
  )
}

export default FormFieldComponent
